/**
 * The navigation model consumed by the shell. One tree, several presentations: the
 * rail renders each group as a hover pop-out, and future consumers (mobile drawer,
 * global search) read the same items so the access rules are only expressed once.
 */
export interface NavItem {
  /** Translation key, or a literal label when the backend supplies the wording. */
  title: string;
  link: string;
  icon?: string;
  customSvg?: string;
  /** Translation key for the one-line summary shown on the section landing page. */
  description?: string;
  /** Translation keys of labels on the page, for search. */
  searchKeywords?: ReadonlyArray<string>;
  /** Renders a separator after this item. */
  hasDividerAfter?: boolean;
}

/**
 * Joins the segments of a destination's menu path. Both sides need it: the model composes the
 * path from translation keys, and the search palette has to split it again to translate and
 * match each segment on its own.
 */
export const NAV_PATH_SEPARATOR = ' › ';

export interface NavSection {
  /** Sub-group heading inside a pop-out, e.g. Pulse within Analytics. */
  label?: string;
  items: NavItem[];
}

/** A destination flattened out of the tree, for search and for cross-context matching. */
export interface NavDestination {
  title: string;
  link: string;
  /** Where it sits in the menu, e.g. "Analytics" or "Analytics › Pulse". */
  path: string;
  /**
   * Identity of the page across contexts: the link with its `/account/<uuid>` or
   * `/venue/<uuid>` prefix removed. Both trees are built from route constants that share
   * their tails, so "campaigns" identifies the same page in either context.
   *
   * Derived rather than declared: the `type` enum on the nav constants is only present on
   * about half the destinations, so matching on it silently failed for the rest.
   */
  pageKey: string;
  icon?: string;
  /**
   * Translation key for its one-line summary. A section's landing page has none: its blurb
   * describes the pages under it, which would each be found twice.
   */
  description?: string;
  /** Translation keys of labels on the page, so it can be found by a field it holds. */
  searchKeywords?: ReadonlyArray<string>;
}

/** Something to do rather than somewhere to go, offered by search where its page is reachable. */
export interface NavAction {
  id: string;
  /** Translation key. */
  title: string;
  icon: string;
  link: string;
  /** Menu path of the page it belongs to, that page included, joined like a destination's. */
  path: string;
}

/**
 * Everything the nav tree depends on. Passing it explicitly lets the tree be built for a
 * context other than the current one — needed to ask "does the account I am switching to have
 * this page too?" before the app has finished moving there.
 */
export interface NavContextInput {
  isAccountContext: boolean;
  contextUuid: string;
  /** The target's own admin flag: `isUserAccountAdmin` or `isUserVenueAdmin`. */
  canSeeSettings: boolean;
  isAiEnabled: boolean;
}

/** Shared by the group, its icon and the route that mounts its landing page. */
export const NAV_GROUP_KEYS = {
  COPILOT: 'copilot',
  ANALYTICS: 'analytics',
  SURVEYS: 'surveys',
  FEATURES: 'features',
  SETTINGS: 'settings',
  ADMIN: 'admin',
} as const;

export type NavGroupKey = (typeof NAV_GROUP_KEYS)[keyof typeof NAV_GROUP_KEYS];

export interface NavGroup {
  key: string;
  title: string;
  icon?: string;
  customSvg?: string;
  /** Translation key for the blurb introducing the group on its landing page. */
  description?: string;
  /** Sets this group apart from the ones above it in the rail. */
  hasDividerBefore?: boolean;
  /** Headings for the landing page's runs of cards, in order. */
  landingLabels?: ReadonlyArray<string>;
  /** Its landing page when it has sub-sections, otherwise its single page. */
  link?: string;
  sections: NavSection[];
}
